// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.
import { Modal } from '@mui/material';
import { type EditPortfolioConfig } from '@advisor-app/features/my-research';
import { EditableText } from '@advisor-app/components';
import { Heading, CloseButton } from '@shared/ui-kit/components';
import { PortfolioPositions } from '@advisor-app/features/portfolio';
import { IconButton } from '@shared/ui-kit/components/icon-button';
import { type FC, useMemo } from 'react';
import { useAddPortfolioModal } from './use-add-portfolio-modal';
import { useTranslation } from 'react-i18next';

export interface AddPortfolioModalProps {
  open: boolean;
  onClose?: VoidFunction;
  editPortfolioConfig: EditPortfolioConfig;
  isEdit: boolean;
}

export const AddPortfolioModal: FC<AddPortfolioModalProps> = ({
  open,
  onClose,
  editPortfolioConfig,
  isEdit,
}) => {
  const { t } = useTranslation();
  const {
    positions,
    updatedBenchmark,
    handleUpdateBenchmark,
    setPositions,
    filteredPositions,
    updatePosition,
    onDeletePositions,
    onSavePortfolio,
    onEditPortfolio,
    onCloseAddPortfolioModal,
    handleEditPortfolioName,
    hasChanges,
  } = useAddPortfolioModal({ onClose, editPortfolioConfig });

  const { portfolioName, portfolioBenchmark } = editPortfolioConfig;

  const totalWeight = useMemo(() => {
    return positions.reduce((acc, position) => acc + (position.weight ?? 0), 0);
  }, [positions]);

  const isSaveDisabled = useMemo(
    () => !positions.length || !hasChanges || totalWeight !== 100,
    [hasChanges, positions.length, totalWeight],
  );

  const isEditDisabled = useMemo(() => isEdit && !hasChanges, [isEdit, hasChanges]);

  const displayBenchmark = useMemo(
    () => updatedBenchmark || portfolioBenchmark,
    [updatedBenchmark, portfolioBenchmark],
  );

  return (
    <Modal open={open} sx={{ overflowY: 'scroll' }} disableEnforceFocus>
      <div className="absolute inset-0 m-auto max-h-[565px] max-w-[1205px] items-center justify-center !rounded-2xl bg-white">
        <div className="relative items-center justify-between rounded-lg bg-white !p-5">
          <CloseButton
            onClick={isEditDisabled ? onCloseAddPortfolioModal : onEditPortfolio}
          />
          <div className="mt-4 px-6">
            <div className="flex justify-between">
              <Heading
                variant="h1"
                title={
                  isEdit
                    ? t('glossary.edit-portfolio', { ns: 'glossary' })
                    : t('glossary.portfolio-upload', { ns: 'glossary' })
                }
              />
              <IconButton
                className="mr-4"
                icon="Save"
                variant="button"
                disabled={isSaveDisabled || isEditDisabled}
                onClick={isEdit ? onEditPortfolio : onSavePortfolio}
                data-testid="save-portfolio-button"
              />
            </div>
            <div className="relative">
              {isEdit && (
                <EditableText
                  text={portfolioName}
                  onEdit={handleEditPortfolioName}
                  className="absolute left-8 top-20 z-10"
                  placeholder={t('glossary.portfolio-name', { ns: 'glossary' })}
                />
              )}
              <PortfolioPositions
                filteredDataTable={filteredPositions}
                handleDeleteAllPositions={onDeletePositions}
                invalidTickers={[]}
                isSomeInvalidTicker={false}
                positionsTableData={positions}
                setPositionTableData={setPositions}
                portfolio={[]}
                typeAsset=""
                updateMyData={updatePosition}
                workflowType=""
                tableHeaderText={
                  isEdit ? '' : t('glossary.insert-portfolio', { ns: 'glossary' })
                }
                scroll
                portfolioBenchmark={displayBenchmark}
                handleUpdateBenchmark={handleUpdateBenchmark}
                mode="weight"
                totalWeight={totalWeight}
              />
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};
